import { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Space,
  Spin,
  message as toast,
  Tooltip,
  Typography,
} from 'antd';
import axios from 'axios';
import {
  CopyOutlined,
  ExpandAltOutlined,
  LinkOutlined,
  NodeIndexOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import ChatBubble from '../../components/ChatBubble';
import { deliveryCaption } from './delivery-caption';

import { ChatInput } from '../../components/ChatInput';
import { phoenixTraceUrl, TraceRef } from './trace-link';
import { formatDateHeader, formatTimeForMessage } from '../agentSms/utils';
import { formatDateWithZone } from './format-date';
import {
  getCallDirectionLabel,
  isIncomingCall,
} from './lead-conversation-call';
import { useAuthenticatedRequest } from '../../UserContext';

import {
  AppstoreOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

type MessageSenderRole = 'lead' | 'ai' | 'agent' | 'other';

interface LeadMessage {
  id: string;
  message: string;
  incoming: boolean;
  created_at: string;
  delivery_status?: string | null;
  error_code?: string | null;
  isCall?: boolean; // Optional, defaults to false
  transcript?: string;
  trace_ids?: Array<{ trace_id: string; timestamp?: string }> | null;
  sender_role?: MessageSenderRole;
}

const SENDER_LABELS: Record<MessageSenderRole, string> = {
  lead: 'Lead',
  ai: 'AI',
  agent: 'Agent',
  other: 'Other',
};

type OutgoingSenderRole = 'ai' | 'agent' | 'other';

const OUTGOING_AVATAR_CONFIG: Record<
  OutgoingSenderRole,
  { icon: React.ReactNode; color: string }
> = {
  ai: { icon: <RobotOutlined />, color: '#1890ff' },
  agent: { icon: <TeamOutlined />, color: '#722ed1' },
  other: { icon: <QuestionCircleOutlined />, color: '#8c8c8c' },
};

interface LeadConversationProps {
  accessToken: string;
  apiUrl: string;
  engagementId: string;
  timezone: string;
  targetMessageId?: string;
}

export const LeadConversation = ({
  accessToken,
  apiUrl,
  engagementId,
  timezone,
  targetMessageId,
}: LeadConversationProps) => {
  const authenticatedRequest = useAuthenticatedRequest(accessToken);
  const [messages, setMessages] = useState<LeadMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<LeadMessage | null>(
    null,
  );

  const openModal = (msg: LeadMessage) => {
    setSelectedMessage(msg);
  };

  const closeModal = () => {
    setSelectedMessage(null);
  };

  const [pendingMessages, setPendingMessages] = useState<LeadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useRef<boolean>(true);
  const linkedMessage = useRef({
    done: false,
    attempts: 0,
    prevCount: 0,
    targetId: null as string | null,
  });
  const [hashKey, setHashKey] = useState(window.location.hash);
  const hashMessageId = hashKey.startsWith('#message-') ? hashKey.slice(1) : '';
  const effectiveTargetMessageId = targetMessageId || hashMessageId || '';

  const resetLinkedMessage = () => {
    linkedMessage.current = {
      done: false,
      attempts: 0,
      prevCount: 0,
      targetId: null,
    };
  };

  const scrollMessageIntoView = (el: HTMLElement) => {
    // Prefer scrolling the message list container; if the modal body is the real scroll container, fall back to it.
    const container =
      listRef.current ||
      (el.closest('.ant-modal-body') as HTMLElement | null) ||
      (document.scrollingElement as HTMLElement | null);

    if (!container) {
      el.scrollIntoView({ block: 'center' });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const deltaTop = elRect.top - containerRect.top;
    const targetTop =
      container.scrollTop +
      deltaTop -
      container.clientHeight / 2 +
      elRect.height / 2;

    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
  };

  const highlightMessage = (el: HTMLElement) => {
    scrollMessageIntoView(el);
    el.classList.remove('pulse-highlight');
    void el.offsetWidth;
    el.classList.add('pulse-highlight');
    setTimeout(() => el.classList.remove('pulse-highlight'), 8000);
  };

  const fetchMessages = async (showLoading = false) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const [responseMessages, responseCalls] = await Promise.all([
        authenticatedRequest(headers =>
          axios.get(`${apiUrl}/api/lead-engagement/${engagementId}/messages`, {
            headers,
          }),
        ),
        authenticatedRequest(headers =>
          axios.get(`${apiUrl}/api/lead-engagement/${engagementId}/calls`, {
            headers,
          }),
        ),
      ]);
      const callsMapped = Array.isArray(responseCalls.data)
        ? responseCalls.data.map((call: any) => ({
            ...call,
            incoming: isIncomingCall(call),
            isCall: true, // flag to distinguish from messages
          }))
        : [];
      const messagesMapped = Array.isArray(responseMessages.data)
        ? responseMessages.data.map((msg: any) => ({
            ...msg,
            isCall: false,
          }))
        : [];

      const messagesWithCalls = [...messagesMapped, ...callsMapped].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      if (messagesWithCalls && messagesWithCalls.length > 0) {
        setMessages(messagesWithCalls);
      } else {
        console.error(
          'Unexpected messages response format:',
          responseMessages.data,
        );
        setMessages([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch messages:', error);
      if (error.response) {
        console.error(
          'Error response:',
          error.response.status,
          error.response.data,
        );
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (engagementId) {
      // Check if we're navigating to a specific message
      if (window.location.hash.startsWith('#message-')) {
        scrollToBottom.current = false;
      } else {
        scrollToBottom.current = true;
      }
      resetLinkedMessage();

      fetchMessages(true);
    }
  }, [apiUrl, engagementId, accessToken]);

  // Listen for hash changes
  useEffect(() => {
    const onHashChange = () => {
      resetLinkedMessage();
      scrollToBottom.current = false;
      setHashKey(window.location.hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (!effectiveTargetMessageId || loading || !messages.length) {
      return;
    }

    if (
      linkedMessage.current.done &&
      linkedMessage.current.targetId === effectiveTargetMessageId
    ) {
      return;
    }

    if (linkedMessage.current.targetId !== effectiveTargetMessageId) {
      linkedMessage.current = {
        done: false,
        attempts: 0,
        prevCount: 0,
        targetId: effectiveTargetMessageId,
      };
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const attempt = (attemptsLeft: number) => {
      if (cancelled) {
        return;
      }
      const el = document.getElementById(effectiveTargetMessageId);
      if (el) {
        linkedMessage.current.done = true;
        linkedMessage.current.targetId = effectiveTargetMessageId;
        highlightMessage(el);
        return;
      }
      if (attemptsLeft > 0) {
        timeoutId = setTimeout(() => attempt(attemptsLeft - 1), 500);
      } else {
        linkedMessage.current.done = true;
        linkedMessage.current.targetId = effectiveTargetMessageId;
        toast.warning('Message not found.');
      }
    };

    attempt(6);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [effectiveTargetMessageId, loading, messages]);

  useEffect(() => {
    // Skip auto-scroll when navigating to a specific message
    if (
      effectiveTargetMessageId ||
      window.location.hash.startsWith('#message-')
    ) {
      return;
    }
    const timeout = setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight + 1;
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [messages, pendingMessages]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Combine server messages and pending messages
  const allMessages = [...messages, ...pendingMessages].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  if (allMessages.length === 0) {
    return (
      <Empty
        description="No messages found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Group messages by date
  const groupedMessages: { [key: string]: LeadMessage[] } = {};
  const seenMessages = new Set<string>();

  allMessages.forEach(msg => {
    // Skip duplicate messages
    if (seenMessages.has(msg.id)) {
      return;
    }
    seenMessages.add(msg.id);

    const date = formatDateHeader(msg.created_at, timezone);
    if (!groupedMessages[date]) {
      groupedMessages[date] = [];
    }
    groupedMessages[date].push(msg);
  });

  const handleSendMessage = async (messageText: string) => {
    if (!engagementId) {
      return;
    }

    setSending(true);
    const tempId = `temp-${Date.now()}`;
    const newMessage: LeadMessage = {
      id: tempId,
      message: messageText,
      incoming: false,
      created_at: new Date().toISOString(),
      sender_role: 'agent',
    };

    try {
      // Optimistic update
      setPendingMessages(prev => [...prev, newMessage]);

      // Scroll to bottom
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }

      // Send the message to the server
      const response = await authenticatedRequest(headers =>
        axios.post(
          `${apiUrl}/api/lead-engagement/${engagementId}/send-message`,
          { message: messageText },
          { headers },
        ),
      );

      // On success, we could immediately fetch messages to get the real one
      // The pending message will be removed, and if fetch succeeds, the real one appears
      setPendingMessages(prev => prev.filter(msg => msg.id !== tempId));
      fetchMessages(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
      // Keep pending message or mark as error? For now, we leave it so user knows it exists?
      // Or remove it? Standard behavior is usually to show error state on the message.
      // Current implementation simply removes it on failure if we don't handle it,
      // but here we only remove it on success (try block continues).
      // If error throws, line `setPendingMessages` removal is skipped.
      // User sees it "stuck" as pending (except we don't distinguish UI for pending yet).
      // Let's remove it to avoid confusion or keep it and let them retry?
      // For simplicity matching previous behavior roughly:
      setPendingMessages(prev => prev.filter(msg => msg.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px 8px 0 0',
          border: '1px solid #e8e8e8',
          borderBottom: 'none',
          maxHeight: 'calc(100vh - 250px)',
        }}
      >
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div
              style={{
                textAlign: 'center',
                margin: '20px 0',
                color: '#999',
                fontSize: '12px',
              }}
            >
              <Tooltip title={timezone}>{date}</Tooltip>
            </div>
            {msgs.map(msg => {
              const senderRole: MessageSenderRole =
                msg.sender_role || (msg.incoming ? 'lead' : 'ai');
              const outgoingRole: OutgoingSenderRole =
                senderRole === 'lead' ? 'ai' : senderRole;
              const outgoingAvatar = OUTGOING_AVATAR_CONFIG[outgoingRole];
              return (
                <div
                  id={`message-${msg.id}`}
                  key={msg.id}
                  style={{
                    display: 'flex',
                    justifyContent: msg.incoming ? 'flex-start' : 'flex-end',
                    marginBottom: 16,
                    alignItems: 'flex-end',
                  }}
                >
                  {msg.incoming && (
                    <Tooltip title="Lead">
                      <Avatar
                        icon={<UserOutlined />}
                        style={{ marginRight: 8, backgroundColor: '#87d068' }}
                      />
                    </Tooltip>
                  )}

                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: msg.incoming ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <div
                      className={`chat-bubble-container ${msg.incoming ? 'incoming' : 'outgoing'}`}
                    >
                      {msg.isCall ? (
                        <ChatBubble
                          isOwnMessage={!msg.incoming}
                          message={`📞 ${getCallDirectionLabel(msg.incoming)}: ${
                            msg.transcript
                              ? msg.transcript.substring(0, 100) + '...'
                              : 'No transcript available'
                          }`}
                          speaker={
                            msg.sender_role
                              ? SENDER_LABELS[senderRole]
                              : undefined
                          }
                          timestamp={formatTimeForMessage(
                            msg.created_at,
                            timezone,
                          )}
                          timestampTooltip={timezone}
                          variant={
                            !msg.incoming && outgoingRole !== 'ai'
                              ? outgoingRole
                              : undefined
                          }
                        />
                      ) : (
                        <ChatBubble
                          isOwnMessage={!msg.incoming}
                          message={msg.message}
                          speaker={
                            msg.sender_role
                              ? SENDER_LABELS[senderRole]
                              : undefined
                          }
                          status={deliveryCaption(msg)}
                          timestamp={formatTimeForMessage(
                            msg.created_at,
                            timezone,
                          )}
                          timestampTooltip={timezone}
                          variant={
                            !msg.incoming && outgoingRole !== 'ai'
                              ? outgoingRole
                              : undefined
                          }
                        />
                      )}
                      {!msg.isCall && (
                        <div className="chat-actions">
                          {msg.trace_ids && msg.trace_ids.length > 0 && (
                            <Button
                              icon={<NodeIndexOutlined />}
                              onClick={e => {
                                e.stopPropagation();
                                const url = phoenixTraceUrl(
                                  msg.trace_ids![0].trace_id,
                                );
                                if (url) {
                                  window.open(
                                    url,
                                    '_blank',
                                    'noopener,noreferrer',
                                  );
                                }
                              }}
                              shape="circle"
                              size="small"
                              title="Open Phoenix trace"
                              type="text"
                            />
                          )}
                          <Button
                            icon={<CopyOutlined />}
                            onClick={e => {
                              e.stopPropagation();
                              if (typeof msg.message === 'string') {
                                void navigator.clipboard.writeText(msg.message);
                                toast.success('Copied!');
                              }
                            }}
                            shape="circle"
                            size="small"
                            title="Copy message"
                            type="text"
                          />
                          <Button
                            icon={<ExpandAltOutlined />}
                            onClick={e => {
                              e.stopPropagation();
                              openModal(msg);
                            }}
                            shape="circle"
                            size="small"
                            title="Open details"
                            type="text"
                          />
                          <Button
                            icon={<LinkOutlined />}
                            onClick={e => {
                              e.stopPropagation();
                              const url = `${window.location.origin}${window.location.pathname}${window.location.search}#message-${msg.id}`;
                              void navigator.clipboard.writeText(url);
                              toast.success('Link copied!');
                            }}
                            shape="circle"
                            size="small"
                            title="Copy link to message"
                            type="text"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {!msg.incoming && (
                    <Tooltip title={SENDER_LABELS[outgoingRole]}>
                      <Avatar
                        icon={outgoingAvatar.icon}
                        style={{
                          marginLeft: 8,
                          backgroundColor: outgoingAvatar.color,
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <ChatInput
        accessToken={accessToken}
        apiUrl={apiUrl}
        confirmSend={true}
        disabled={!engagementId || loading}
        engagementId={engagementId}
        loading={sending}
        onSend={handleSendMessage}
      />

      <Modal
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 16 }}
        footer={null}
        onCancel={closeModal}
        open={!!selectedMessage}
        title={`📩 Message Details - ${selectedMessage?.id}`}
        width={700}
      >
        {selectedMessage && (
          <div style={{ paddingRight: 8 }}>
            {/* Message Content */}
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={<Typography.Text strong>📩 Message</Typography.Text>}
            >
              {selectedMessage.message}
            </Card>

            {/* Timestamp and direction */}
            <Card bordered={false} size="small" style={{ marginBottom: 16 }}>
              <Typography.Text strong>
                <ClockCircleOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                />
                Timestamp:
              </Typography.Text>{' '}
              <Typography.Text>
                {formatTimeForMessage(selectedMessage.created_at, timezone)}
              </Typography.Text>
              <Divider type="vertical" />
              <Typography.Text strong>
                <ExclamationCircleOutlined
                  style={{
                    marginRight: 8,
                    color: selectedMessage.incoming ? '#52c41a' : '#f5222d',
                  }}
                />
                {selectedMessage.incoming ? 'Incoming' : 'Outgoing'}
              </Typography.Text>
              {selectedMessage.sender_role && (
                <>
                  <Divider type="vertical" />
                  <Typography.Text strong>
                    Sender: {SENDER_LABELS[selectedMessage.sender_role]}
                  </Typography.Text>
                </>
              )}
            </Card>

            {/* Phoenix Traces */}
            {selectedMessage?.trace_ids &&
              selectedMessage.trace_ids.length > 0 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <Typography.Text strong>🔍 Phoenix Traces</Typography.Text>
                  }
                >
                  {selectedMessage.trace_ids.map(
                    (trace: any, index: number) => (
                      <div key={index} style={{ marginBottom: 8 }}>
                        <Typography.Link
                          href={phoenixTraceUrl(trace.trace_id) ?? undefined}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Trace {index + 1}
                        </Typography.Link>
                        {trace.timestamp && (
                          <Typography.Text
                            style={{ marginLeft: 8 }}
                            type="secondary"
                          >
                            {formatDateWithZone(trace.timestamp, timezone)}
                          </Typography.Text>
                        )}
                      </div>
                    ),
                  )}
                </Card>
              )}
          </div>
        )}
      </Modal>
    </div>
  );
};
